// Datos del motor de comisiones para las pantallas Comisiones y Comisiones pagadas.
import {
    useIsFetching,
    useMutation,
    useQueries,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import type { RetailCommission } from '@fiado/type-kit';
import { ApiError } from '@/api/errors';
import { useAuth } from '@/hooks/useAuth';
import { attemptForScope } from '@/lib/idempotentAttempt';
import {
    beginWrite,
    clearWriteDoubt,
    readWriteDoubt,
    restoreWriteSnapshot,
    saveWriteDoubt,
    takeWriteSnapshot,
    useWriteDoubt,
    type WriteDoubt,
} from '@/lib/writeDoubt';
import {
    commissionEngine,
    type RuleAction,
} from '@/api/services/retail-commission/commissions.client';
import { useRosterRetailer } from '@/domains/retail/admin/lib/useRoster';

const ROOT = ['retail-admin', 'comisiones-motor'] as const;
const STALE_MS = 60_000;

export const commissionKeys = {
    all: () => ROOT,
    ruleSet: () => [...ROOT, 'plan'] as const,
    rules: () => [...ROOT, 'rules'] as const,
    ruleHistory: (ruleId: string) => [...ROOT, 'rule-history', ruleId] as const,
    policies: () => [...ROOT, 'policies'] as const,
    live: () => [...ROOT, 'live'] as const,
    liveFor: (periodId: string | undefined) => [...ROOT, 'live', periodId ?? 'actual'] as const,
    liveSeller: (sellerId: string, periodId: string | undefined) =>
        [...ROOT, 'live', periodId ?? 'actual', sellerId] as const,
    payouts: () => [...ROOT, 'payouts'] as const,
    payout: (periodId: string) => [...ROOT, 'payouts', periodId] as const,
    payoutSeller: (periodId: string, sellerId: string) =>
        [...ROOT, 'payouts', periodId, sellerId] as const,
    allAggregates: () => [...ROOT, 'aggregates'] as const,
    aggregates: (by: string, from: string, to: string) =>
        [...ROOT, 'aggregates', by, from, to] as const,
    allSeries: () => [...ROOT, 'series'] as const,
    series: (by: string, id: string, weeks: number) => [...ROOT, 'series', by, id, weeks] as const,
    accrualBySale: (saleId: string) => [...ROOT, 'accrual', saleId] as const,
    payments: () => [...ROOT, 'payments'] as const,
    paymentAttention: () => [...ROOT, 'payments', 'attention'] as const,
    sellerLedger: (sellerId: string) => [...ROOT, 'payments', 'ledger', sellerId] as const,
    allStatements: () => [...ROOT, 'statements'] as const,
    statements: (retailerId: string | null) =>
        [...ROOT, 'statements', retailerId ?? 'propia'] as const,
    statement: (statementId: string, retailerId: string | null) =>
        [...ROOT, 'statements', retailerId ?? 'propia', statementId] as const,
};

// ── Reglas, plan y políticas ──

export function useRuleSet() {
    return useQuery({
        queryKey: commissionKeys.ruleSet(),
        queryFn: commissionEngine.getRuleSet,
        staleTime: STALE_MS,
    });
}

export function useRules() {
    return useQuery({
        queryKey: commissionKeys.rules(),
        queryFn: commissionEngine.listRules,
        staleTime: STALE_MS,
    });
}

export function useRuleHistory(ruleId: string | null) {
    return useQuery({
        queryKey: commissionKeys.ruleHistory(ruleId ?? ''),
        queryFn: () => commissionEngine.ruleHistory(ruleId ?? ''),
        enabled: ruleId !== null,
        staleTime: STALE_MS,
    });
}

/** El historial de cada regla, para la auditoría: el motor no tiene uno global. */
export function useRuleHistories(ruleIds: readonly string[]) {
    return useQueries({
        queries: ruleIds.map((ruleId) => ({
            queryKey: commissionKeys.ruleHistory(ruleId),
            queryFn: () => commissionEngine.ruleHistory(ruleId),
            staleTime: STALE_MS,
        })),
    });
}

export function usePolicies() {
    return useQuery({
        queryKey: commissionKeys.policies(),
        queryFn: commissionEngine.getPolicies,
        staleTime: STALE_MS,
    });
}

/** Lo que mueve una regla publicada mueve el plan vigente y el corte vivo. */
function invalidateAfterRuleChange(qc: ReturnType<typeof useQueryClient>, ruleId: string | null) {
    void qc.invalidateQueries({ queryKey: commissionKeys.rules() });
    void qc.invalidateQueries({ queryKey: commissionKeys.ruleSet() });
    if (ruleId) void qc.invalidateQueries({ queryKey: commissionKeys.ruleHistory(ruleId) });
    void qc.invalidateQueries({ queryKey: commissionKeys.live() });
}

/** 409: la regla o el plan cambiaron desde que la pantalla los cargó. */
export function isRuleConflict(error: unknown): boolean {
    return error instanceof ApiError && error.httpStatus === 409;
}

export function useCreateRule() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: RetailCommission.CreateRuleRequest) => commissionEngine.createRule(body),
        onSuccess: ({ rule }) => {
            invalidateAfterRuleChange(qc, rule.ruleId);
        },
    });
}

export function useUpdateRule() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({
            ruleId,
            body,
        }: {
            ruleId: string;
            body: RetailCommission.UpdateRuleRequest;
        }) => commissionEngine.updateRule(ruleId, body),
        onSuccess: (_data, { ruleId }) => {
            invalidateAfterRuleChange(qc, ruleId);
        },
        onError: (error, { ruleId }) => {
            if (isRuleConflict(error)) invalidateAfterRuleChange(qc, ruleId);
        },
    });
}

export function useRuleAction() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({
            ruleId,
            action,
            expectedVersion,
        }: {
            ruleId: string;
            action: RuleAction;
            expectedVersion: number;
        }) => commissionEngine.ruleAction(ruleId, action, expectedVersion),
        onSuccess: (_data, { ruleId }) => {
            invalidateAfterRuleChange(qc, ruleId);
        },
        onError: (error, { ruleId }) => {
            if (isRuleConflict(error)) invalidateAfterRuleChange(qc, ruleId);
        },
    });
}

export function useDeleteRule() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (ruleId: string) => commissionEngine.deleteRule(ruleId),
        onSuccess: (_data, ruleId) => {
            invalidateAfterRuleChange(qc, ruleId);
        },
        onError: (error, ruleId) => {
            if (isRuleConflict(error)) invalidateAfterRuleChange(qc, ruleId);
        },
    });
}

export function useUpdatePolicies() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: RetailCommission.UpdatePoliciesRequest) =>
            commissionEngine.updatePolicies(body),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: commissionKeys.policies() });
            void qc.invalidateQueries({ queryKey: commissionKeys.ruleSet() });
            void qc.invalidateQueries({ queryKey: commissionKeys.live() });
        },
    });
}

export function useSimulateRules() {
    return useMutation({
        mutationFn: (body: RetailCommission.SimulateRulesRequest) =>
            commissionEngine.simulateRules(body),
    });
}

// ── Corte vivo y cortes cerrados ──

/** 404: el vendedor no tiene nada en ese corte; la pantalla lo lee como semana sin movimientos. */
export function isSellerNotInPayout(error: unknown): boolean {
    return error instanceof ApiError && error.httpStatus === 404;
}

/** 409: el corte ya no recibe ajustes porque se está cerrando o ya cerró. */
export function isPayoutNotOpen(error: unknown): boolean {
    return (
        error instanceof ApiError &&
        error.httpStatus === 409 &&
        (error.code === 'PAYOUT_CLOSE_IN_PROGRESS' || error.code === 'PAYOUT_ALREADY_CLOSED')
    );
}

export function useLivePayout(periodId?: string, enabled = true) {
    return useQuery({
        queryKey: commissionKeys.liveFor(periodId),
        queryFn: () => commissionEngine.getLivePayout(periodId),
        enabled,
        staleTime: STALE_MS,
    });
}

export function useLiveSeller(sellerId: string | null, periodId?: string) {
    return useQuery({
        queryKey: commissionKeys.liveSeller(sellerId ?? '', periodId),
        queryFn: () => commissionEngine.getLiveSeller(sellerId ?? '', periodId),
        enabled: sellerId !== null,
        staleTime: STALE_MS,
        // Un vendedor sin nada en ese corte responde 404: es una semana sin movimientos.
        retry: false,
    });
}

export function usePayouts(limit = 12) {
    return useQuery({
        queryKey: [...commissionKeys.payouts(), 'list', limit],
        queryFn: () => commissionEngine.listPayouts(limit),
        staleTime: STALE_MS,
    });
}

export function usePayout(periodId: string | null) {
    return useQuery({
        queryKey: commissionKeys.payout(periodId ?? ''),
        queryFn: () => commissionEngine.getPayout(periodId ?? ''),
        enabled: periodId !== null,
        staleTime: STALE_MS,
    });
}

export function usePayoutSeller(periodId: string | null, sellerId: string | null) {
    return useQuery({
        queryKey: commissionKeys.payoutSeller(periodId ?? '', sellerId ?? ''),
        queryFn: () => commissionEngine.getPayoutSeller(periodId ?? '', sellerId ?? ''),
        enabled: periodId !== null && sellerId !== null,
        staleTime: STALE_MS,
        retry: false,
    });
}

export function usePayoutAggregates(
    by: `${RetailCommission.CommissionAggregateByEnum}`,
    from: string | null,
    to: string | null,
) {
    return useQuery({
        queryKey: commissionKeys.aggregates(by, from ?? '', to ?? ''),
        queryFn: () => commissionEngine.getAggregates(by, from ?? '', to ?? ''),
        enabled: from !== null && to !== null,
        staleTime: STALE_MS,
    });
}

export function usePayoutSeries(
    by: `${RetailCommission.CommissionSeriesByEnum}`,
    id: string | null,
    weeks: number,
) {
    return useQuery({
        queryKey: commissionKeys.series(by, id ?? '', weeks),
        queryFn: () => commissionEngine.getSeries(by, id ?? '', weeks),
        enabled: id !== null,
        staleTime: STALE_MS,
    });
}

/** El motor lo rechazó sin escribirlo. Un 408 o un 409 no: la misma llave puede seguir en vuelo. */
const isDefinitiveRejection = (error: unknown) =>
    error instanceof ApiError &&
    error.httpStatus >= 400 &&
    error.httpStatus < 500 &&
    error.httpStatus !== 408 &&
    error.httpStatus !== 409;

/** Lo que la pantalla hace al terminar: va en la mutación para que corra aunque el diálogo se cierre. */
export interface WriteHandlers {
    onSuccess?: () => void;
    onError?: (error: unknown) => void;
}

/** De quién son las escrituras: el tenant y la cadena del usuario, para que una duda no las cruce. */
function useWriteOwner(): string {
    const { user } = useAuth();
    const { retailerId } = useRosterRetailer();
    return `${user?.tenantId ?? ''}:${retailerId ?? ''}`;
}

/** La huella del intento es el recurso, no el cuerpo: un solo envío en vuelo por recurso. */
const RESOURCE_WRITE = 'write';

interface WriteRules {
    /** 409: la llave ya registró otra cosa. */
    isKeyReused: (error: unknown) => boolean;
    /** Rechazos que prueban que el motor ya buscó la llave y no escribió nada. */
    keyLookedUp: (error: unknown) => boolean;
}

/** Lo que se envía y la duda que mostró la pantalla al enviarlo. */
export interface MoneyWrite<B> {
    sellerId: string;
    body: B;
    /** Con ella, la caducidad la decide un solo reloj: el de la pantalla. */
    shown?: WriteDoubt<B> | null;
}

/**
 * Escritura de dinero con la llave del recurso (no del cuerpo). La llave se guarda en el navegador
 * antes de enviar; tras un fallo sin respuesta clara, reenviar, aunque sea otro monto, viaja con la
 * misma llave y el motor no lo registra dos veces.
 */
function writeOnce<T>(
    scope: string,
    body: { amountCents: number },
    send: (key: string) => Promise<T>,
    rules: WriteRules,
    shown?: WriteDoubt<unknown> | null,
): Promise<T> {
    const attempt = attemptForScope(scope);
    let doubt: { key: string; at: number | null } | null = shown ?? null;
    if (doubt === null) {
        const stored = readWriteDoubt(scope);
        if (stored.kind === 'expired') {
            // Esa llave ya no se puede confirmar: el envío nuevo lleva otra.
            clearWriteDoubt(scope);
            attempt.renew(RESOURCE_WRITE);
        } else if (stored.kind === 'doubt') doubt = stored.doubt;
    }
    const key = doubt?.key ?? attempt.keyFor(RESOURCE_WRITE);
    const record = { key, at: doubt?.at ?? Date.now(), amountCents: body.amountCents };
    const before = takeWriteSnapshot(scope);
    // Un doble clic recibe la petición en vuelo; solo quien la mandó toca la duda.
    let started = false;
    let written = '';
    return attempt
        .run(RESOURCE_WRITE, () => {
            started = true;
            written = beginWrite(scope, record, body);
            return send(key);
        })
        .then(
            (result) => {
                if (started) clearWriteDoubt(scope);
                return result;
            },
            (error: unknown) => {
                if (!started) throw error;
                if (rules.isKeyReused(error)) {
                    // Lo registrado con esa llave ya movió el saldo: el siguiente envío lleva otra.
                    attempt.renew(RESOURCE_WRITE);
                    clearWriteDoubt(scope);
                } else if (rules.keyLookedUp(error)) clearWriteDoubt(scope);
                else if (isDefinitiveRejection(error)) {
                    // Este envío no escribió: sin duda previa no queda nada; con ella, queda como
                    // estaba (un 400, 401, 403 o 404 no prueba nada del envío anterior).
                    if (doubt === null) clearWriteDoubt(scope);
                    else restoreWriteSnapshot(scope, before, written);
                } else saveWriteDoubt(scope, record, body);
                throw error;
            },
        );
}

/** Al cerrar el formulario el siguiente envío es otra escritura, salvo con la duda viva. */
function settleWrite(scope: string) {
    const state = readWriteDoubt(scope);
    if (state.kind === 'doubt') return;
    if (state.kind === 'expired') clearWriteDoubt(scope);
    attemptForScope(scope).settle();
}

/** La duda de una escritura para su formulario. */
export interface ResourceWriteDoubt<B> {
    /** Lo enviado que falló sin respuesta clara; `null` si no hay duda. */
    doubt: WriteDoubt<B> | null;
    /** Pasó el plazo de la llave: ese intento ya no se puede confirmar. */
    expired: boolean;
    /** Al cerrar el formulario. */
    settle: () => void;
}

function useResourceWriteDoubt<B>(scope: string): ResourceWriteDoubt<B> {
    const state = useWriteDoubt<B>(scope);
    return {
        doubt: state.kind === 'doubt' ? state.doubt : null,
        expired: state.kind === 'expired',
        settle: () => {
            settleWrite(scope);
        },
    };
}

const adjustmentScope = (owner: string, periodId: string, sellerId: string) =>
    `commission-adjustment:${owner}:${periodId}:${sellerId}`;

/** 409: la llave ya registró un ajuste con otro monto o motivo. */
export const isAdjustmentKeyReused = (error: unknown) =>
    error instanceof ApiError && error.code === 'ADJUSTMENT_KEY_REUSED';

/** El ajuste de un vendedor en un corte que falló sin respuesta clara. */
export function useAdjustmentDoubt(periodId: string, sellerId: string) {
    return useResourceWriteDoubt<RetailCommission.CreateAdjustmentRequest>(
        adjustmentScope(useWriteOwner(), periodId, sellerId),
    );
}

/** Una llave por vendedor y corte; tras el éxito, llave nueva; un doble clic reusa la petición. */
export function useCreatePayoutAdjustment(periodId: string | null, handlers: WriteHandlers = {}) {
    const qc = useQueryClient();
    const owner = useWriteOwner();
    // Mueve el neto del vendedor, el total del corte, el corte vivo, lo pagado y su pendiente.
    const invalidateAfterAdjustment = () => {
        void qc.invalidateQueries({ queryKey: commissionKeys.payouts() });
        void qc.invalidateQueries({ queryKey: commissionKeys.live() });
        void qc.invalidateQueries({ queryKey: commissionKeys.allAggregates() });
        void qc.invalidateQueries({ queryKey: commissionKeys.allSeries() });
        void qc.invalidateQueries({ queryKey: commissionKeys.payments() });
    };
    return useMutation({
        mutationFn: ({
            sellerId,
            body,
            shown,
        }: MoneyWrite<RetailCommission.CreateAdjustmentRequest>) => {
            if (periodId === null) return Promise.reject(new Error('El ajuste necesita un corte.'));
            // El vendedor es el del formulario, no el del cuerpo.
            const sent = { ...body, sellerId };
            return writeOnce(
                adjustmentScope(owner, periodId, sellerId),
                sent,
                (key) => commissionEngine.createAdjustment(periodId, sent, key),
                // Con el corte cerrándose o cerrado, el motor ya buscó la llave.
                { isKeyReused: isAdjustmentKeyReused, keyLookedUp: isPayoutNotOpen },
                shown,
            );
        },
        onError: (error) => {
            // El corte ya cambió: el siguiente intento tiene que ir al corte vigente.
            if (isPayoutNotOpen(error))
                void qc.invalidateQueries({ queryKey: commissionKeys.live() });
            // Lo que se registró con esa llave ya movió el corte.
            if (isAdjustmentKeyReused(error)) invalidateAfterAdjustment();
            handlers.onError?.(error);
        },
        onSuccess: () => {
            invalidateAfterAdjustment();
            handlers.onSuccess?.();
        },
    });
}

/** El CSV que arma el motor para la nómina de la cadena. */
export function useExportPayout() {
    return useMutation({
        mutationFn: (periodId: string) => commissionEngine.exportPayout(periodId),
    });
}

// ── Devengo de una venta ──

export function useAccrualBySale(saleId: string, sellerId: string | null, soldAt: string | null) {
    return useQuery({
        queryKey: [...commissionKeys.accrualBySale(saleId), sellerId, soldAt],
        queryFn: () => commissionEngine.getAccrualBySale(saleId, sellerId ?? '', soldAt ?? ''),
        enabled: sellerId !== null && soldAt !== null,
        staleTime: STALE_MS,
        retry: false,
    });
}

// ── Pagos a la wallet ──

export function usePaymentAttention(enabled = true) {
    return useQuery({
        queryKey: commissionKeys.paymentAttention(),
        queryFn: commissionEngine.getPaymentAttention,
        enabled,
        staleTime: STALE_MS,
    });
}

/** 404 `SELLER_LEDGER_NOT_FOUND`: el vendedor todavía no tiene saldo; otro 404 es un error. */
export function isLedgerMissing(error: unknown): boolean {
    return (
        error instanceof ApiError &&
        error.httpStatus === 404 &&
        error.code === 'SELLER_LEDGER_NOT_FOUND'
    );
}

export function useSellerLedger(sellerId: string | null, enabled = true) {
    return useQuery({
        queryKey: commissionKeys.sellerLedger(sellerId ?? ''),
        queryFn: () => commissionEngine.getSellerLedger(sellerId ?? ''),
        enabled: enabled && sellerId !== null,
        staleTime: STALE_MS,
        retry: false,
    });
}

/** Un pago mueve el reporte Por pagar y el saldo de la ficha. */
function invalidateAfterPayment(qc: ReturnType<typeof useQueryClient>) {
    void qc.invalidateQueries({ queryKey: commissionKeys.payments() });
}

export function useRetrySellerPayment(handlers: WriteHandlers = {}) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (sellerId: string) => commissionEngine.retrySellerPayment(sellerId),
        onSuccess: () => {
            invalidateAfterPayment(qc);
            handlers.onSuccess?.();
        },
        onError: handlers.onError,
    });
}

const paidOutsideScope = (owner: string, sellerId: string) =>
    `commission-paid-outside:${owner}:${sellerId}`;

/** 409: la llave ya registró un pago por fuera con otro monto o motivo. */
export const isPaidOutsideKeyReused = (error: unknown) =>
    error instanceof ApiError && error.code === 'PAID_OUTSIDE_KEY_REUSED';

/** 422: el monto pasa del exigible de hoy; el motor ya buscó la llave y no la encontró. */
const isPaidOutsideOverPayable = (error: unknown) =>
    error instanceof ApiError && error.code === 'PAID_OUTSIDE_EXCEEDS_PAYABLE';

/** El pago por fuera de un vendedor que falló sin respuesta clara. */
export function usePaidOutsideDoubt(sellerId: string) {
    return useResourceWriteDoubt<RetailCommission.MarkPaidOutsideRequest>(
        paidOutsideScope(useWriteOwner(), sellerId),
    );
}

/** Una llave por vendedor: tras un fallo dudoso, cualquier reenvío viaja con ella. */
export function useMarkPaidOutside(handlers: WriteHandlers = {}) {
    const qc = useQueryClient();
    const owner = useWriteOwner();
    return useMutation({
        mutationFn: ({
            sellerId,
            body,
            shown,
        }: MoneyWrite<RetailCommission.MarkPaidOutsideRequest>) =>
            writeOnce(
                paidOutsideScope(owner, sellerId),
                body,
                (key) => commissionEngine.markPaidOutside(sellerId, body, key),
                { isKeyReused: isPaidOutsideKeyReused, keyLookedUp: isPaidOutsideOverPayable },
                shown,
            ),
        onSuccess: () => {
            invalidateAfterPayment(qc);
            handlers.onSuccess?.();
        },
        onError: (error) => {
            // Lo registrado con esa llave ya movió el saldo, o el pendiente cambió desde que se leyó.
            if (isPaidOutsideKeyReused(error) || isPaidOutsideOverPayable(error))
                invalidateAfterPayment(qc);
            handlers.onError?.(error);
        },
    });
}

/** "Revisar el pendiente": vuelve a leer Por pagar y el saldo de la ficha, y dice si está leyendo. */
export function useRefreshPayments() {
    const qc = useQueryClient();
    const refreshing = useIsFetching({ queryKey: commissionKeys.payments() }) > 0;
    return {
        refresh: () => {
            invalidateAfterPayment(qc);
        },
        refreshing,
    };
}

/** 409: otro ya revisó el depósito con el resultado contrario. */
const isPaymentAlreadyResolved = (error: unknown) =>
    error instanceof ApiError && error.code === 'PAYMENT_ALREADY_RESOLVED';

/** Un depósito revisado sale de Por pagar y, si se confirmó, entra al estado de cuenta. */
function invalidateAfterResolve(qc: ReturnType<typeof useQueryClient>) {
    invalidateAfterPayment(qc);
    void qc.invalidateQueries({ queryKey: commissionKeys.allStatements() });
}

export function useResolvePayment(handlers: WriteHandlers = {}) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({
            paymentId,
            body,
        }: {
            paymentId: string;
            body: RetailCommission.ResolvePaymentRequest;
        }) => commissionEngine.resolvePayment(paymentId, body),
        onSuccess: () => {
            invalidateAfterResolve(qc);
            handlers.onSuccess?.();
        },
        onError: (error) => {
            // Ya no está por revisar: la lista tiene que dejar de mostrarlo.
            if (isPaymentAlreadyResolved(error)) invalidateAfterResolve(qc);
            handlers.onError?.(error);
        },
    });
}

// ── Estados de cuenta ──

/** `retailerId` en `null`: la cadena del token (Admin VL). */
export function useStatements(retailerId: string | null, enabled = true) {
    return useQuery({
        queryKey: commissionKeys.statements(retailerId),
        queryFn: () => commissionEngine.listStatements(retailerId ?? undefined),
        enabled,
        staleTime: STALE_MS,
    });
}

export function useStatement(statementId: string | null, retailerId: string | null) {
    return useQuery({
        queryKey: commissionKeys.statement(statementId ?? '', retailerId),
        queryFn: () => commissionEngine.getStatement(statementId ?? '', retailerId ?? undefined),
        enabled: statementId !== null,
        staleTime: STALE_MS,
    });
}

/** El CSV del estado de cuenta, para cobrarle a la cadena. */
export function useExportStatement() {
    return useMutation({
        mutationFn: ({
            statementId,
            retailerId,
        }: {
            statementId: string;
            retailerId: string | null;
        }) => commissionEngine.exportStatement(statementId, retailerId ?? undefined),
    });
}
